/*
 * *****************************************************************************
 *  Copyright (C)  Motorola Solutions, INC.
 *  All Rights Reserved.
 *  Motorola Solutions Confidential Restricted.
 *  ******************************************************************************
 */

import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, filter, map } from 'rxjs/operators';
import {
    sendCallMessageFail,
    sendDtmfMessage,
    sendRttMessage,
    sendSmsMessage,
    sendTddMessage,
    translateAndSendMessage
} from '../../call/+state/call.actions';
import {
    fetchTranslationLanguagesFail,
    fetchTranslationLanguagesSuccess,
} from './message.actions';
import { TextTranslationService } from '../services/text-translation.service';
import { EnvironmentService } from '../../core/services/environment.service';
import { fetchTranslationSettingsSuccess } from '../../configuration/+state/configuration.actions';
import { MessageType } from "CalltakingCoreApi";

const MESSAGE_TYPE = {
    TDD: 'TDD',
    RTT: 'RTT',
    SMS: 'SMS',
    DTMF: 'DTMF',
} as const satisfies Record<string, MessageType>;

@Injectable()
export class TranslationEffects {

    private readonly defaultLang: string = this.envService.environment?.defaultLanguage ?? 'en';

    constructor(
        private actions$: Actions,
        private translationService: TextTranslationService,
        private envService: EnvironmentService
    ) {}

    fetchTranslationLanguages$: Observable<{}> = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchTranslationSettingsSuccess),
            filter(({ translationEnabled }) => {
                const envFlag = !!(this.envService.environment?.featureFlags?.translationsEnabled);
                return envFlag && translationEnabled;
            }),
            concatMap(() =>
                this.translationService.getLanguages().pipe(
                    map((languages) => fetchTranslationLanguagesSuccess({ languages })),
                    catchError((err) => {
                        console.error('fetchTranslationLanguages$ — error', err);
                        return of(fetchTranslationLanguagesFail({ payload: String(err) }));
                    })
                )
            )
        )
    );

    translateAndSendMessage$: Observable<{}> = createEffect(() =>
        this.actions$.pipe(
            ofType(translateAndSendMessage),
            concatMap(({ callId, clusterName, message, messageType, predefined, language }) => {
                    const participant = { type: 'CALL_TAKER' as const };
                    const messageId = this.generateMessageId();
                    console.debug(`translateAndSendMessage$ [OUT] — calling translateMessage() callId=${callId} messageId=${messageId} text="${this.anonymizeForLog(message)}"`);
                    return this.translationService.translateMessage(callId, messageId, message, participant).pipe(
                        map(({ translatedText, sourceLanguage, targetLanguage }) => {
                            const translationApplied = translatedText !== null;
                            const sentOriginal = translationApplied && targetLanguage === this.defaultLang;
                            const msgToSend = translationApplied
                                ? (sentOriginal ? message : (translatedText ?? message))
                                : message;

                            const coreSourceText     = translationApplied && !sentOriginal ? message         : undefined;
                            const coreSourceLanguage = translationApplied && !sentOriginal ? (sourceLanguage ?? undefined) : undefined;
                            const coreTargetText     = translationApplied && sentOriginal  ? (translatedText ?? undefined) : undefined;
                            const coreTargetLanguage = translationApplied && sentOriginal  ? (targetLanguage ?? undefined) : undefined;
                            const coreMessageLanguage = translationApplied
                                ? (sentOriginal ? (sourceLanguage ?? undefined) : (targetLanguage ?? undefined))
                                : undefined;

                            console.debug(
                                'translateAndSendMessage$ [OUT] — sending'
                                + ` callId=${callId}`
                                + ` translationApplied=${translationApplied}`
                                + ` sentOriginal=${sentOriginal}`
                                + ` msgToSend="${this.anonymizeForLog(msgToSend)}"`
                                + ` coreSourceText="${this.anonymizeForLog(coreSourceText)}"`
                                + ` coreTargetText="${this.anonymizeForLog(coreTargetText)}"`
                                + ` coreMessageLanguage="${coreMessageLanguage}"`
                            );

                            return this.buildSendMessageAction(
                                messageType, msgToSend, callId, clusterName, predefined, language,
                                translationApplied ? messageId : undefined,
                                coreSourceText, coreSourceLanguage, coreTargetText, coreTargetLanguage, coreMessageLanguage
                            );
                        }),
                        catchError((err) => {
                            console.error(`translateAndSendMessage$ [OUT] — translateMessage error callId=${callId}`, err);
                            return of(this.buildSendMessageAction(messageType, message, callId, clusterName, predefined, language));
                        })
                    );
            })
        )
    );

    private buildSendMessageAction(messageType: MessageType, message: string, callId: string, clusterName: string, predefined: boolean, language: string,
                                   clientMessageId?: string, sourceText?: string, sourceLanguage?: string, targetText?: string, targetLanguage?: string, messageLanguage?: string) {
        switch (messageType) {
            case MESSAGE_TYPE.TDD:
                return sendTddMessage({ message, clusterName, callId, predefined, language, clientMessageId, sourceText, sourceLanguage, targetText, targetLanguage, messageLanguage });
            case MESSAGE_TYPE.RTT:
                return sendRttMessage({ message, clusterName, callId, predefined, language });
            case MESSAGE_TYPE.SMS:
                return sendSmsMessage({ message, clusterName, callId, predefined, language, clientMessageId, sourceText, sourceLanguage, targetText, targetLanguage, messageLanguage });
            case MESSAGE_TYPE.DTMF:
                return sendDtmfMessage({ message, clusterName, callId });
            default:
                return sendCallMessageFail({ payload: 'Unable to find or determine call message type.' });
        }
    }

    private generateMessageId() : string {
        return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }

    private anonymizeForLog(text: string | null | undefined): string {
        if (text == null) {
            return 'null...';
        }
        return `${text.slice(0, 5)}...`;
    }
}
